import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { queryKeys } from '../../lib/queryKeys';

export interface OverdueBill {
  id: string;
  company_id: string;
  bill_id_external: string;
  doc_number: string | null;
  vendor_name: string;
  vendor_id_external: string | null;
  bill_date: string;
  due_date: string | null;
  total_amount: number;
  balance_due: number;
  currency: string;
  status: string;
  source: string;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OverdueBillsSummary {
  totalOverdue: number;
  billCount: number;
  bills: OverdueBill[];
  currency: string;
  averageDaysOverdue: number;
}

async function fetchOverdueBills(companyId: string): Promise<OverdueBillsSummary> {
  const { data, error: fetchError } = await supabase
    .from('bills')
    .select('*')
    .eq('company_id', companyId)
    .eq('status', 'overdue')
    .order('due_date', { ascending: true });

  if (fetchError) throw fetchError;

  const bills = data || [];
  const totalOverdue = bills.reduce((sum, bill) => sum + (bill.balance_due || 0), 0);

  // Calculate days overdue manually
  const today = new Date();
  const averageDaysOverdue =
    bills.length > 0
      ? bills.reduce((sum, bill) => {
          if (bill.due_date) {
            const dueDate = new Date(bill.due_date);
            const daysOverdue = Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
            return sum + Math.max(0, daysOverdue);
          }
          return sum;
        }, 0) / bills.length
      : 0;

  return {
    totalOverdue,
    billCount: bills.length,
    bills,
    currency: bills[0]?.currency || 'USD',
    averageDaysOverdue: Math.round(averageDaysOverdue),
  };
}

export function useOverdueBills(companyId: string | undefined) {
  const query = useQuery({
    queryKey: queryKeys.bills.overdue(companyId || ''),
    queryFn: () => fetchOverdueBills(companyId!),
    enabled: !!companyId,
  });

  return {
    summary: query.data,
    isLoading: query.isLoading,
    error: query.error?.message || null,
    refetch: query.refetch,
  };
}
